<template>
  <div class="email-designer">
    <div class="email-designer__editor"
         ref="editor"></div>
  </div>
</template>

<script>
  /*
   * Wrapper um GrapesJS + grapesjs-mjml.
   *
   * - value: { design, mjml }  design = GrapesJS-Projektdaten (JSON-String), mjml = MJML-Quelltext
   * - getValue() liefert beides für das Speichern; das HTML rendert der Server (Mjml.Net).
   * - Marken-Blöcke, Platzhalter-Menü und Button-Ziele werden aus den Props gespeist.
   */
  import grapesjs from 'grapesjs'
  import grapesjsMjml from 'grapesjs-mjml'
  import 'grapesjs/dist/css/grapes.min.css'
  import de from 'grapesjs/locale/de'

  import { createBrandBlocks, expandSelfClosingMjmlTags } from './email-designer-blocks'

  const MJML_DE = {
    'grapesjs-mjml': {
      category: 'Allgemein',
      components: {
        names: {
          body: 'Body',
          button: 'Button',
          column: 'Spalte',
          divider: 'Trennlinie',
          group: 'Gruppe',
          hero: 'Hero',
          image: 'Bild',
          navBar: 'Navigation',
          navLink: 'Navigationslink',
          oneColumn: '1 Spalte',
          raw: 'Rohcode',
          section: 'Abschnitt',
          socialGroup: 'Social-Gruppe',
          socialElement: 'Social-Element',
          spacer: 'Abstand',
          text: 'Text',
          threeColumn: '3 Spalten',
          twoColumn: '2 Spalten',
          wrapper: 'Wrapper'
        }
      },
      panels: {
        buttons: {
          desktop: 'Desktop',
          export: 'MJML/HTML anzeigen',
          import: 'MJML importieren',
          mobile: 'Mobil',
          redo: 'Wiederholen',
          tablet: 'Tablet',
          undo: 'Rückgängig',
          'sw-visibility': 'Komponentenrahmen anzeigen',
          preview: 'Vorschau',
          fullscreen: 'Vollbild'
        },
        export: {
          title: 'Code'
        },
        import: {
          title: 'MJML importieren',
          button: 'Importieren',
          label: 'MJML hier einfügen'
        }
      }
    }
  }

  export default {
    beforeDestroy() {
      if (this.editor) {
        this.editor.destroy()
        this.editor = null
      }
    },
    computed: {
      audiencePlaceholders() {
        return (this.placeholders || []).filter(p => !p.audiences || p.audiences.length === 0 || p.audiences.includes(this.audience))
      },
      audienceTargets() {
        return (this.callToActionTargets || []).filter(t => !t.audience || t.audience === this.audience)
      },
      /**
       * Platzhalter, die eine Bild-URL liefern (derzeit der persönliche QR-Code). Im Canvas wird die Beispiel-URL
       * des Servers gezeigt; gespeichert wird der Platzhalter. Der Benutzer wählt sie am Bild über „Bildquelle“.
       */
      displayImagePlaceholders() {
        return (this.placeholders || [])
          .filter(p => p.key === 'qrCodeUrl' && p.example)
          .map(p => ({
            key: p.key,
            label: p.label,
            badge: this.$t('dynamicImageBadge', { label: p.label }),
            placeholder: `{{${p.key}}}`,
            url: p.example
          }))
      }
    },
    data: () => ({
      editor: null
    }),
    methods: {
      /**
       * Liefert Design (JSON) und MJML für das Speichern; Anzeige-URLs werden wieder zu Platzhaltern.
       * GrapesJS übernimmt Text, der gerade im Canvas getippt wird, erst beim Beenden der Bearbeitung ins Modell.
       * Ein noch aktives Textfeld wird deshalb vorher abgeschlossen, sonst würde der alte Text exportiert.
       */
      async getValue() {
        await this.flushEditing()

        return {
          design: this.collapseDisplayUrls(JSON.stringify(this.editor.getProjectData())),
          mjml: this.collapseDisplayUrls(this.editor.getHtml())
        }
      },
      /** Beendet eine laufende Textbearbeitung, damit der getippte Inhalt im Modell landet. */
      async flushEditing() {
        const editing = this.editor && this.editor.getEditing()

        if (editing && editing.view && typeof editing.view.disableEditing === 'function') {
          await editing.view.disableEditing()
        }
      },
      /**
       * Lädt eine Vorlage: bevorzugt Projektdaten, sonst MJML-Import.
       * Gespeichert wird umgebungsneutral ({{baseUrl}}, {{apiBaseUrl}}); für die Anzeige im Canvas werden die
       * Platzhalter durch die URLs der aktuellen Umgebung ersetzt. Der Server neutralisiert sie beim Speichern wieder.
       */
      load({ design, mjml }) {
        design = this.expandEnvironmentUrls(design)
        mjml = this.expandEnvironmentUrls(mjml)

        if (design) {
          try {
            this.editor.loadProjectData(JSON.parse(design))
            this.editor.UndoManager.clear()

            return
          } catch (e) {
            // Design nicht lesbar → auf MJML zurückfallen
          }
        }

        if (mjml) {
          this.editor.setComponents(mjml)
        }

        this.editor.UndoManager.clear()
      },
      expandEnvironmentUrls(text) {
        let result = (text || '')
          .replace(/{{apiBaseUrl}}/g, (this.apiBaseUrl || '').replace(/\/$/, ''))
          .replace(/{{baseUrl}}/g, (this.baseUrl || '').replace(/\/$/, ''))

        // Bild-Platzhalter (z. B. {{qrCodeUrl}}) kann der Browser nicht laden: im Canvas die Beispiel-URL zeigen.
        this.displayImagePlaceholders.forEach(({ placeholder, url }) => {
          result = result.split(placeholder).join(url)
        })

        return result
      },
      /** Umkehrung von expandEnvironmentUrls für Bild-Platzhalter; Basis-URLs neutralisiert der Server. */
      collapseDisplayUrls(text) {
        let result = text || ''

        this.displayImagePlaceholders.forEach(({ placeholder, url }) => {
          result = result.split(url).join(placeholder)
        })

        return result
      },
      /** Nach Tab-Wechsel: Canvas neu vermessen. */
      refresh() {
        if (this.editor) {
          this.editor.refresh()
        }
      },
      addPlaceholderAction(editor) {
        const options = this.audiencePlaceholders
          .map(p => `<option value="${p.key}">${p.label}</option>`)
          .join('')

        editor.RichTextEditor.add('placeholder', {
          event: 'change',
          icon: `<select class="gjs-field email-designer__placeholder" title="${this.$t('placeholder')}">
            <option value="">${this.$t('placeholder')}</option>${options}
          </select>`,
          result: (rte, action) => {
            const select = action.btn.firstChild

            if (select.value) {
              rte.insertHTML(`{{${select.value}}}`)
              select.value = ''
            }
          },
          update: (rte, action) => {
            action.btn.firstChild.value = ''
          }
        })
      },
      /**
       * Feld „Ziel“ für Buttons. In die Vorlage kommt nur der Platzhalter {{link:Key}}; die URL der jeweiligen
       * Umgebung setzt der Server beim Rendern ein. Das Feld zeigt immer das Ziel, das aktuell im href steht.
       */
      addButtonTargetTrait(editor) {
        const targets = this.audienceTargets
        const label = this.$t('target')
        const targetFromHref = href => {
          const match = /^{{link:([A-Za-z0-9_-]+)}}$/.exec(href || '')

          return match ? match[1] : ''
        }

        editor.on('component:selected', component => {
          if (!component || component.get('type') !== 'mj-button') {
            return
          }

          const current = targetFromHref(component.getAttributes().href)

          if (component.get('traits').where({ name: 'sf-target' }).length > 0) {
            component.set('sf-target', current)

            return
          }

          component.addTrait({
            changeProp: true,
            label,
            name: 'sf-target',
            options: [ { id: '', name: this.$t('customLink') }, ...targets.map(t => ({ id: t.key, name: t.label })) ],
            type: 'select'
          }, { at: 0 })
          component.set('sf-target', current)

          component.on('change:sf-target', () => {
            const target = targets.find(t => t.key === component.get('sf-target'))

            if (target) {
              component.addAttributes({ href: target.placeholder })
              component.components(target.buttonText)
            } else if (targetFromHref(component.getAttributes().href)) {
              // „Eigener Link“ gewählt: Platzhalter entfernen, damit eine URL eingetragen werden kann.
              component.addAttributes({ href: '' })
            }
          })
        })
      },
      /**
       * Feld „Bildquelle“ für Bilder: eigenes Bild oder ein dynamischer Platzhalter wie der persönliche QR-Code.
       * Das Feld zeigt immer, was aktuell im src steht; die Auswahl setzt die Beispiel-URL (Anzeige) und den Alt-Text.
       */
      addImageSourceTrait(editor) {
        const options = this.displayImagePlaceholders
        const label = this.$t('imageSource')
        const sourceFromSrc = src => (options.find(o => o.url === src) || {}).key || ''

        editor.on('component:selected', component => {
          if (!component || component.get('type') !== 'mj-image') {
            return
          }

          const current = sourceFromSrc(component.getAttributes().src)

          if (component.get('traits').where({ name: 'sf-image' }).length > 0) {
            component.set('sf-image', current)

            return
          }

          component.addTrait({
            changeProp: true,
            label,
            name: 'sf-image',
            options: [ { id: '', name: this.$t('ownImage') }, ...options.map(o => ({ id: o.key, name: o.label })) ],
            type: 'select'
          }, { at: 0 })
          component.set('sf-image', current)

          component.on('change:sf-image', () => {
            const option = options.find(o => o.key === component.get('sf-image'))

            if (option) {
              component.addAttributes({ src: option.url, alt: option.label })
            } else if (sourceFromSrc(component.getAttributes().src)) {
              // „Eigenes Bild“ gewählt: Platzhalter entfernen, Bild kann per Doppelklick aus dem Asset-Manager gewählt werden.
              component.addAttributes({ src: '' })
            }
          })
        })
      },
      /** Markiert dynamische Bilder im Canvas (Rahmen + Hinweis) und benennt sie in Badge/Ebenen entsprechend. */
      markDynamicImages(editor) {
        const options = this.displayImagePlaceholders
        const mark = component => {
          if (!component || component.get('type') !== 'mj-image') {
            return
          }

          const option = options.find(o => o.url === component.getAttributes().src)
          const cell = component.getEl() && component.getEl().firstElementChild

          component.set('custom-name', option ? option.label : '')

          if (cell) {
            cell.classList.toggle('sf-dynamic-image', !!option)
            option ? cell.setAttribute('data-sf-label', option.badge) : cell.removeAttribute('data-sf-label')
          }
        }

        editor.on('component:mount', mark)
        editor.on('component:update:attributes', mark)
      },
      configureImageAltTrait(editor) {
        // Alt-Text als Pflichtangabe sichtbar machen (Bilder werden in Mail-Clients oft blockiert).
        editor.on('component:selected', component => {
          if (!component || component.get('type') !== 'mj-image') {
            return
          }

          const alt = component.get('traits').where({ name: 'alt' })[0]

          if (alt && !alt.get('placeholder')) {
            alt.set('placeholder', this.$t('altRequired'))
          }
        })
      }
    },
    mounted() {
      const assets = (this.assets || []).map(a => ({
        height: a.height,
        name: a.name || a.fileName,
        src: a.src || a.url,
        type: 'image',
        width: a.width
      }))

      const editor = grapesjs.init({
        assetManager: {
          assets,
          autoAdd: true,
          // Anmeldung läuft über den Bearer-Token, nicht über Cookies. Der GrapesJS-Standard ('include') lässt den
          // Browser den Upload am CORS-Preflight scheitern, weil die API mit Access-Control-Allow-Origin: * antwortet.
          credentials: 'omit',
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          },
          multiUpload: true,
          upload: this.uploadUrl,
          uploadName: 'files'
        },
        container: this.$refs.editor,
        fromElement: false,
        height: '100%',
        parser: {
          optionsHtml: {
            // Der HTML-Parser des Browsers kennt keine selbstschließenden mj-Tags: Aus "<mj-image … />" wird ein
            // offenes Tag, und alles Folgende (z. B. die Kontaktdaten unter einem Portrait) landet als Kind darin.
            // MJML ignoriert solche Kinder beim Rendern. Deshalb werden die Tags vor dem Parsen aufgeklappt.
            preParser: expandSelfClosingMjmlTags
          }
        },
        i18n: {
          locale: 'de',
          messages: {
            de: { ...de, ...MJML_DE }
          }
        },
        plugins: [ grapesjsMjml ],
        pluginsOpts: {
          [grapesjsMjml]: {
            columnsPadding: '10px 0',
            imagePlaceholderSrc: `${this.baseUrl}/images/email-logo.png`,
            importPlaceholder: '<mjml><mj-body></mj-body></mjml>',
            useCustomTheme: true
          }
        },
        storageManager: false,
        // Nur im Canvas: dynamische Bilder (Platzhalter) sichtbar als solche markieren.
        canvasCss: `
          td.sf-dynamic-image { outline: 2px dashed #113e7c; outline-offset: -2px; position: relative; }
          td.sf-dynamic-image::before { content: attr(data-sf-label); position: absolute; left: 0; top: 0; z-index: 1;
            background: #113e7c; color: #ffffff; font: 11px/1.4 Helvetica, Arial, sans-serif; padding: 2px 6px; }
        `
      })

      // Marken-Bausteine vor den generischen MJML-Blöcken einsortieren
      const blocks = createBrandBlocks({
        baseUrl: this.baseUrl,
        category: this.$t('brandBlocks'),
        targets: this.audienceTargets,
        audience: this.audience
      })

      blocks.forEach((block, index) => editor.BlockManager.add(block.id, { ...block, order: index }))

      // Marken-Bausteine als erste Kategorie anzeigen, generische MJML-Blöcke eingeklappt darunter.
      const brandCategory = this.$t('brandBlocks')
      editor.BlockManager.getCategories().each(category => {
        const isBrand = category.getLabel() === brandCategory
        category.set({ open: isBrand, order: isBrand ? 0 : 1 })
      })

      this.addPlaceholderAction(editor)
      this.addButtonTargetTrait(editor)
      this.addImageSourceTrait(editor)
      this.markDynamicImages(editor)
      this.configureImageAltTrait(editor)

      if (!this.showCode) {
        // Code-Ansicht und MJML-Import nur für technische Benutzer
        try {
          editor.Panels.removeButton('options', 'export-template')
          editor.Panels.removeButton('options', 'mjml-import')
        } catch (e) {
          // Buttons existieren je nach Plugin-Version nicht
        }
      }

      editor.on('update', () => this.$emit('dirty'))

      this.editor = editor
      this.load(this.value || {})
      this.$emit('ready', editor)
    },
    name: 'EmailDesigner',
    props: {
      accessToken: String,
      apiBaseUrl: String,
      assets: Array,
      audience: {
        default: 'Customer',
        type: String
      },
      baseUrl: String,
      callToActionTargets: Array,
      placeholders: Array,
      showCode: Boolean,
      uploadUrl: String,
      value: Object
    }
  }
</script>

<style>
  /* GrapesJS liegt außerhalb des Vuetify-Scopes, daher ungescoped und mit gjs-Präfix */
  .email-designer {
    border: 1px solid var(--sf-border);
    border-radius: 16px;
    height: calc(100vh - 340px);
    min-height: 560px;
    overflow: hidden;
  }

  .email-designer__editor {
    height: 100%;
  }

  .email-designer .gjs-one-bg {
    background-color: #1f2937;
  }

  .email-designer .gjs-pn-btn.gjs-pn-active,
  .email-designer .gjs-four-color,
  .email-designer .gjs-four-color-h:hover {
    color: #e3000a;
  }

  .email-designer .email-designer__placeholder {
    background-color: rgba(255, 255, 255, 0.95);
    border-radius: 4px;
    color: #1f2937;
    font-size: 12px;
    margin-left: 6px;
    max-width: 160px;
    padding: 2px 4px;
  }

  .email-designer .gjs-rte-action:has(.email-designer__placeholder) {
    width: auto;
  }

  /* Vuetify setzt global "background-repeat: no-repeat"; das Schachbrett hinter transparenten Bildern
     im Asset-Manager zeigte dadurch nur eine einzelne Kachel in der Ecke. */
  .email-designer .gjs-checker-bg,
  .email-designer .gjs-am-preview-bg {
    background-repeat: repeat;
  }
</style>

<i18n>
  {
    "de": {
      "altRequired": "Bitte Alternativtext angeben",
      "customLink": "Eigener Link",
      "imageSource": "Bildquelle",
      "ownImage": "Eigenes Bild",
      "dynamicImageBadge": "{label} – wird beim Versand je Empfänger erzeugt",
      "brandBlocks": "SAGASSER-Bausteine",
      "placeholder": "Platzhalter…",
      "target": "Ziel"
    },
    "en": {
      "altRequired": "Please provide alternative text",
      "customLink": "Custom link",
      "imageSource": "Image source",
      "ownImage": "Own image",
      "dynamicImageBadge": "{label} – generated per recipient when sending",
      "brandBlocks": "SAGASSER blocks",
      "placeholder": "Placeholder…",
      "target": "Target"
    }
  }
</i18n>
